import { EducationalTooltip } from '@/components/educational-tooltip';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { AddRounded, InfoOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { TicketStatus } from '@/ticket/api/types';
import { CreateTicketDialog } from '@/ticket/components/create-ticket-dialog';
import { TicketTable } from '@/ticket/components/ticket-table';
import { TICKET_STATUS_OPTIONS } from '@/ticket/constants/status-filter-options';
import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

export function TicketList() {
  const [status, setStatus] = useState<TicketStatus>('OPEN');

  const handleStatusChange = (newStatus: TicketStatus) => setStatus(newStatus);

  const dialog = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack>
      {/* Action bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={status}
            options={TICKET_STATUS_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel="Ticket status filter"
          />
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Learn about tickets"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>

        {/* Educational tooltip explaining tickets */}
        <EducationalTooltip
          title="Tickets"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Tickets</strong> track support requests, action items,
                repairs, investigations, and follow-up tasks. Tickets provide a
                structured way to manage work that needs to be done, whether
                related to specific assets, projects, or general organizational
                tasks.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Ticket status
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Open:</strong> The ticket is active and requires
                  attention.
                </li>
                <li>
                  <strong>Closed:</strong> The ticket has been completed and
                  closed.
                </li>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Ticket categories
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Todo:</strong> General tasks or items that need to be
                  completed.
                </li>
                <li>
                  <strong>Check:</strong> Inspection or verification tasks.
                </li>
                <li>
                  <strong>Defect:</strong> Issues or problems that need to be
                  addressed.
                </li>
                <li>
                  <strong>Repair:</strong> Maintenance or repair work required
                  on equipment.
                </li>
                <li>
                  <strong>Miscellaneous:</strong> Items that don't fit other
                  categories.
                </li>
              </Typography>
            </Box>
            <Typography variant="body2">
              <Link
                href="https://guide.hardfin.com/run-your-business/tickets"
                target="_blank"
              >
                Read more about tickets
              </Link>
            </Typography>
          </Stack>
        </EducationalTooltip>

        <Button
          variant="contained"
          startIcon={<AddRounded fontSize="small" />}
          onClick={dialog.open}
        >
          Open ticket
        </Button>
      </Stack>

      <CreateTicketDialog isOpen={dialog.isOpen} onClose={dialog.close} />

      <TicketTable status={status} />
    </Stack>
  );
}
